import {
  CarrierAccountType,
  EasyPostCarrierType,
  RedoCarrierName,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import SearchSm from "@redotech/redo-icons/search-sm.svg";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { RedoBaseModal } from "../../../arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "../../../arbiter-components/modal/redo-modal";
import { TeamContext } from "../../../context/team-context";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { LogoSize, ShippingLogo } from "../../common/shipping-logo";
import * as styles from "./add-carrier-modal.module.css";
import {
  AmazonCarrierForm,
  CreateAmazonCarrierForm,
} from "./carrier-forms/create-amazon-carrier-form";
import {
  ApcCarrierForm,
  CreateApcCarrierForm,
} from "./carrier-forms/create-apc-carrier-form";
import {
  AsendiaCarrierForm,
  CreateAsendiaCarrierForm,
} from "./carrier-forms/create-asendia-carrier-form";
import {
  AustraliaPostCarrierForm,
  CreateAustraliaPostCarrierForm,
} from "./carrier-forms/create-australia-post-carrier-form";
import {
  CanadaPostCarrierForm,
  CreateCanadaPostCarrierForm,
} from "./carrier-forms/create-canada-post-carrier-form";
import {
  CanadaPostDirectCarrierForm,
  CreateCanadaPostDirectCarrierForm,
} from "./carrier-forms/create-canada-post-direct-carrier-form";
import {
  CanparCarrierForm,
  CreateCanparCarrierForm,
} from "./carrier-forms/create-canpar-carrier-form";
import {
  ChitChatsCarrierForm,
  CreateChitChatsCarrierForm,
} from "./carrier-forms/create-chitchats-carrier-form";
import {
  CreateDHLCarrierForm,
  DHLCarrierForm,
} from "./carrier-forms/create-dhl-carrier-form";
import {
  CreateDHLExpressCarrierForm,
  DHLExpressCarrierForm,
} from "./carrier-forms/create-dhl-express-carrier-form";
import {
  CreateEcomExpressCarrierForm,
  EcomExpressCarrierForm,
} from "./carrier-forms/create-ecom-express-carrier-form";
import {
  CreateEPostGlobalCarrierForm,
  EPostGlobalCarrierForm,
} from "./carrier-forms/create-epost-global-carrier-form";
import {
  CreateETAExpressCarrierForm,
  ETAExpressCarrierForm,
} from "./carrier-forms/create-eta-express-carrier-form";
import {
  CreateFedexCarrierForm,
  FedexCarrierForm,
} from "./carrier-forms/create-fedex-carrier-form";
import {
  CreateFedexCrossBorderCarrierForm,
  FedexCrossBorderCarrierForm,
} from "./carrier-forms/create-fedex-international-carrier-form";
import {
  CreateFedexSmartpostCarrierForm,
  FedexSmartpostCarrierForm,
} from "./carrier-forms/create-fedex-smartpost-carrier-form";
import {
  CreateFirstMileCarrierForm,
  FirstMileCarrierForm,
} from "./carrier-forms/create-first-mile-carrier-form";
import {
  CreateGlsCarrierForm,
  GlsCarrierForm,
} from "./carrier-forms/create-gls-carrier-form";
import {
  CreateJitsuCarrierForm,
  JitsuCarrierForm,
} from "./carrier-forms/create-jitsu-carrier-form";
import {
  CreateOnTracCarrierForm,
  OnTracCarrierForm,
} from "./carrier-forms/create-ontrac-carrier-form";
import {
  CreatePassportGlobalCarrierForm,
  PassportGlobalCarrierForm,
} from "./carrier-forms/create-passport-global-carrier-form";
import {
  CreatePurolatorCarrierForm,
  PurolatorCarrierForm,
} from "./carrier-forms/create-purolator-carrier-form";
import {
  CreateRoyalMailCarrierForm,
  RoyalMailCarrierForm,
} from "./carrier-forms/create-royal-mail-carrier-form";
import {
  CreateRoyalMailV3CarrierForm,
  RoyalMailV3CarrierForm,
} from "./carrier-forms/create-royal-mail-v3-carrier-form";
import {
  CreateSekoOmniParcelCarrierForm,
  SekoOmniParcelCarrierForm,
} from "./carrier-forms/create-seko-omni-parcel-carrier-form";
import {
  CreateSendcloudCarrierForm,
  SendcloudCarrierForm,
} from "./carrier-forms/create-sendcloud-carrier-form";
import {
  CreateSpeeDeeCarrierForm,
  SpeeDeeCarrierForm,
} from "./carrier-forms/create-spee-dee-carrier-form";
import {
  CreateStallionExpressCarrierForm,
  StallionExpressCarrierForm,
} from "./carrier-forms/create-stallion-express-carrier-form";
import {
  CreateUniUniCarrierForm,
  UniUniCarrierForm,
} from "./carrier-forms/create-uniuni-carrier-form";
import {
  CreateUPSCarrierForm,
  CreateUPSSurepostCarrierForm,
  UPSCarrierForm,
  UPSSurepostCarrierForm,
} from "./carrier-forms/create-ups-carrier-form";
import {
  CreateUpsMailInnovationsCarrierForm,
  UpsMailInnovationsCarrierForm,
} from "./carrier-forms/create-ups-mail-innovations-carrier-form";
import {
  CreateUSPSCarrierForm,
  USPSCarrierForm,
} from "./carrier-forms/create-usps-carrier-form";
import {
  CreateUspsDirectCarrierForm,
  UspsDirectCarrierForm,
} from "./carrier-forms/create-usps-direct-carrier-form";
import {
  CreateUspsShipCarrierForm,
  UspsShipCarrierForm,
} from "./carrier-forms/create-usps-ship-carrier-form";
import {
  CreateVehoCarrierForm,
  VehoCarrierForm,
} from "./carrier-forms/create-veho-carrier-form";
import { CarrierInputForm, CarrierInputValue } from "./carrier-input";
import { carrierCardLabel, carrierCardLogoCarrier } from "./carrier-labels";
import { getFilteredCarrierOptions } from "./carrier-options";

/** The selected carrier's specific input form (shared by modal + inline card forms). */
export function carrierSpecificInput(newCarrier: CarrierInputForm) {
  const carrierToInput: Record<
    RedoCarrierName,
    | DHLCarrierForm
    | DHLExpressCarrierForm
    | UPSCarrierForm
    | UPSSurepostCarrierForm
    | UpsMailInnovationsCarrierForm
    | USPSCarrierForm
    | FedexCrossBorderCarrierForm
    | FedexCarrierForm
    | FedexSmartpostCarrierForm
    | VehoCarrierForm
    | ApcCarrierForm
    | FirstMileCarrierForm
    | AustraliaPostCarrierForm
    | CanadaPostCarrierForm
    | CanadaPostDirectCarrierForm
    | RoyalMailCarrierForm
    | RoyalMailV3CarrierForm
    | SekoOmniParcelCarrierForm
    | GlsCarrierForm
    | AsendiaCarrierForm
    | SpeeDeeCarrierForm
    | AmazonCarrierForm
    | UspsShipCarrierForm
    | UspsDirectCarrierForm
    | PassportGlobalCarrierForm
    | EPostGlobalCarrierForm
    | ETAExpressCarrierForm
    | OnTracCarrierForm
    | PurolatorCarrierForm
    | UniUniCarrierForm
    | StallionExpressCarrierForm
    | EcomExpressCarrierForm
    | ChitChatsCarrierForm
    | SendcloudCarrierForm
    | CanparCarrierForm
    | JitsuCarrierForm
  > = {
    [RedoCarrierName.DHL_ECS]: newCarrier.inputs.dhlCarrierInput,
    [RedoCarrierName.DHL_EXPRESS]: newCarrier.inputs.dhlExpressCarrierInput,
    [RedoCarrierName.UPS]: newCarrier.inputs.upsCarrierInput,
    [RedoCarrierName.UPS_SUREPOST]: newCarrier.inputs.upsSurepostCarrierInput,
    [RedoCarrierName.UPS_MAIL_INNOVATIONS]:
      newCarrier.inputs.upsMailInnovationsCarrierInput,
    [RedoCarrierName.USPS]: newCarrier.inputs.uspsCarrierInput,
    [RedoCarrierName.FEDEX_CROSS_BORDER]:
      newCarrier.inputs.fedexCrossBorderCarrierInput,
    [RedoCarrierName.FEDEX]: newCarrier.inputs.fedexCarrierInput,
    [RedoCarrierName.FEDEX_SMARTPOST]:
      newCarrier.inputs.fedexSmartpostCarrierInput,
    [RedoCarrierName.VEHO]: newCarrier.inputs.vehoCarrierInput,
    [RedoCarrierName.APC]: newCarrier.inputs.apcCarrierInput,
    [RedoCarrierName.FIRST_MILE]: newCarrier.inputs.firstMileCarrierInput,
    [RedoCarrierName.AUSTRALIA_POST]:
      newCarrier.inputs.australiaPostCarrierInput,
    [RedoCarrierName.CANADA_POST]: newCarrier.inputs.canadaPostCarrierInput,
    [RedoCarrierName.CANADA_POST_DIRECT]:
      newCarrier.inputs.canadaPostDirectCarrierInput,
    [RedoCarrierName.ROYAL_MAIL]: newCarrier.inputs.royalMailCarrierInput,
    [RedoCarrierName.ROYAL_MAIL_V3]: newCarrier.inputs.royalMailV3CarrierInput,
    [RedoCarrierName.SEKO_OMNIPARCEL]:
      newCarrier.inputs.sekoOmniParcelCarrierInput,
    [RedoCarrierName.GLS]: newCarrier.inputs.glsCarrierInput,
    [RedoCarrierName.ASENDIA]: newCarrier.inputs.asendiaCarrierInput,
    [RedoCarrierName.SPEE_DEE]: newCarrier.inputs.speeDeeCarrierInput,
    [RedoCarrierName.AMAZON]: newCarrier.inputs.amazonCarrierInput,
    [RedoCarrierName.USPS_SHIP]: newCarrier.inputs.uspsShipCarrierInput,
    [RedoCarrierName.USPS_DIRECT]: newCarrier.inputs.uspsDirectCarrierInput,
    [RedoCarrierName.PASSPORT_GLOBAL]:
      newCarrier.inputs.passportGlobalCarrierInput,
    [RedoCarrierName.EPOST_GLOBAL]: newCarrier.inputs.epostGlobalCarrierInput,
    [RedoCarrierName.ETA_EXPRESS]: newCarrier.inputs.etaExpressCarrierInput,
    [RedoCarrierName.ONTRAC]: newCarrier.inputs.onTracCarrierInput,
    [RedoCarrierName.PUROLATOR]: newCarrier.inputs.purolatorCarrierInput,
    [RedoCarrierName.UNIUNI]: newCarrier.inputs.uniUniCarrierInput,
    [RedoCarrierName.STALLION_EXPRESS]:
      newCarrier.inputs.stallionExpressCarrierInput,
    [RedoCarrierName.ECOM_EXPRESS]: newCarrier.inputs.ecomExpressCarrierInput,
    [RedoCarrierName.CHITCHATS]: newCarrier.inputs.chitChatsCarrierInput,
    [RedoCarrierName.SENDCLOUD]: newCarrier.inputs.sendcloudCarrierInput,
    [RedoCarrierName.CANPAR_EXPRESS]: newCarrier.inputs.canparCarrierInput,
    [RedoCarrierName.JITSU]: newCarrier.inputs.jitsuCarrierInput,
  };
  return carrierToInput[newCarrier.inputs.redoCarrierName.value];
}

export const AddCarrierModal = memo(function AddCarrierModal({
  carrierOptions,
  onClose,
  isOpen,
  selectedCarrier,
  setSelectedCarrier,
  newCarrier,
  onAddCarrier,
  carrierAccountType,
  onOpenFedexMfaModal,
  onOpenEasypostFedexMfaForCreate,
  sendcloudByoaEnabled = false,
}: {
  carrierOptions: RedoCarrierName[];
  onClose: () => void;
  isOpen: boolean;
  selectedCarrier?: RedoCarrierName;
  setSelectedCarrier: (
    carrier: React.SetStateAction<RedoCarrierName | undefined>,
  ) => void;
  newCarrier: CarrierInputForm;
  onAddCarrier: (carrier: CarrierInputValue) => Promise<{ success: boolean }>;
  carrierAccountType: CarrierAccountType;
  onOpenFedexMfaModal?: () => void;
  onOpenEasypostFedexMfaForCreate?: (carrierType: EasyPostCarrierType) => void;
  sendcloudByoaEnabled?: boolean;
}) {
  const team = useContext(TeamContext);
  const [searchText, setSearchText] = useState("");

  const filteredCarrierOptions = useMemo(() => {
    return getFilteredCarrierOptions({
      carrierOptions,
      checkoutOptimizationEnabled:
        team?.settings?.checkoutOptimization?.enabled,
      newOmsEnabled: team?.settings?.outboundLabels?.newOmsEnabled,
      returnsEnabled: team?.settings?.returns?.enabled,
      sendcloudByoaEnabled,
    });
  }, [
    carrierOptions,
    team?.settings?.checkoutOptimization?.enabled,
    team?.settings?.outboundLabels?.newOmsEnabled,
    team?.settings?.returns?.enabled,
    sendcloudByoaEnabled,
  ]);

  const searchedCarrierOptions = useMemo(() => {
    if (!searchText) {
      return filteredCarrierOptions;
    }
    const lower = searchText.toLowerCase();
    return filteredCarrierOptions.filter((carrier) =>
      carrierCardLabel(carrier).toLowerCase().includes(lower),
    );
  }, [filteredCarrierOptions, searchText]);

  const handleClose = useCallback(() => {
    setSearchText("");
    onClose();
  }, [onClose]);

  const canSubmit = useMemo(() => {
    return (
      newCarrier.inputs.name.allErrors.length === 0 &&
      carrierSpecificInput(newCarrier).allErrors.length === 0
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCarrier.allErrors, newCarrier.inputs.redoCarrierName.value]);

  let title = "";
  let subtitle = "";

  switch (carrierAccountType) {
    case CarrierAccountType.MERCHANT:
      title = "Bring your own carrier";
      break;
    case CarrierAccountType.REDO_CHILD:
      title = "Add a Redo child carrier account";
      break;
    default:
      assertNever(carrierAccountType);
  }

  switch (carrierAccountType) {
    case CarrierAccountType.MERCHANT:
      subtitle = "Add a supported carrier account to bring your own rates";
      break;
    case CarrierAccountType.REDO_CHILD:
      subtitle =
        "Add a Redo child carrier account to support Redo rates for accounts that have different credentials per merchant";
      break;
    default:
      assertNever(carrierAccountType);
  }

  return (
    <RedoBaseModal
      isOpen={isOpen}
      onModalCloseRequested={handleClose}
      otherClasses={[styles.carrierModal]}
    >
      <RedoModalHeader
        cancelClicked={handleClose}
        subtitle={subtitle}
        title={title}
      />
      <Flex
        bgColor="secondary"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        borderTopWidth="1px"
        w="full"
      >
        {selectedCarrier ? (
          <Flex dir="column" gap="xl" p="3xl" w="full">
            <Flex align="center" dir="row" gap="lg">
              <ShippingLogo
                carrier={carrierCardLogoCarrier(selectedCarrier)}
                size={LogoSize.MEDIUM}
              />
              <Text fontSize="md" fontWeight="semibold">
                {carrierCardLabel(selectedCarrier)}
              </Text>
            </Flex>
            <AddCarrierForm newCarrier={newCarrier} />
          </Flex>
        ) : (
          <Flex
            dir="column"
            gap="xl"
            style={{
              height: "500px",
              overflow: "auto",
              padding: "var(--redo-spacing-3xl) var(--redo-spacing-3xl) 24px",
            }}
            w="full"
          >
            <RedoTextInput
              autoFocus
              IconLeading={SearchSm}
              placeholder="Search"
              setValue={setSearchText}
              value={searchText}
            />
            <div className={styles.carrierGrid}>
              {searchedCarrierOptions.length === 0 ? (
                <Text fontSize="sm" textColor="tertiary">
                  No carriers found
                </Text>
              ) : (
                searchedCarrierOptions.map((carrier) => (
                  <CarrierCard
                    carrier={carrier}
                    key={carrier}
                    onClick={(carrier: RedoCarrierName) => {
                      if (
                        (carrier === RedoCarrierName.FEDEX ||
                          carrier === RedoCarrierName.FEDEX_SMARTPOST) &&
                        !!onOpenEasypostFedexMfaForCreate
                      ) {
                        const carrierType =
                          carrier === RedoCarrierName.FEDEX
                            ? EasyPostCarrierType.FEDEX
                            : EasyPostCarrierType.FEDEX_SMARTPOST;
                        onOpenEasypostFedexMfaForCreate(carrierType);
                        handleClose();
                      } else if (
                        carrier === RedoCarrierName.FEDEX &&
                        !!onOpenFedexMfaModal
                      ) {
                        onOpenFedexMfaModal();
                        handleClose();
                      } else {
                        setSelectedCarrier(carrier);
                        newCarrier.inputs.redoCarrierName.setValue(carrier);
                        newCarrier.inputs.name.setValue(
                          carrierCardLabel(carrier),
                        );
                      }
                    }}
                  />
                ))
              )}
            </div>
          </Flex>
        )}
      </Flex>
      {selectedCarrier && (
        <RedoModalFooter
          primaryButton={{
            onClick: async () => {
              const { success } = await onAddCarrier(newCarrier.value);
              if (success) {
                handleClose();
              }
            },
            text: "Add carrier account",
            size: "sm",
            disabled: !canSubmit,
          }}
          secondaryButton={{
            onClick: () => setSelectedCarrier(undefined),
            text: "Back",
            size: "sm",
          }}
        />
      )}
    </RedoBaseModal>
  );
});

const carrierSpecificForm = (newCarrier: CarrierInputForm) => {
  switch (newCarrier.inputs.redoCarrierName.value) {
    case RedoCarrierName.DHL_ECS:
      return (
        <CreateDHLCarrierForm
          carrierInput={newCarrier.inputs.dhlCarrierInput}
        />
      );
    case RedoCarrierName.DHL_EXPRESS:
      return (
        <CreateDHLExpressCarrierForm
          carrierInput={newCarrier.inputs.dhlExpressCarrierInput}
        />
      );
    case RedoCarrierName.UPS:
      return (
        <CreateUPSCarrierForm
          carrierInput={newCarrier.inputs.upsCarrierInput}
        />
      );
    case RedoCarrierName.UPS_SUREPOST:
      return (
        <CreateUPSSurepostCarrierForm
          carrierInput={newCarrier.inputs.upsSurepostCarrierInput}
        />
      );
    case RedoCarrierName.UPS_MAIL_INNOVATIONS:
      return (
        <CreateUpsMailInnovationsCarrierForm
          carrierInput={newCarrier.inputs.upsMailInnovationsCarrierInput}
        />
      );
    case RedoCarrierName.USPS:
      return (
        <CreateUSPSCarrierForm
          carrierInput={newCarrier.inputs.uspsCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX:
      return (
        <CreateFedexCarrierForm
          carrierInput={newCarrier.inputs.fedexCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX_SMARTPOST:
      return (
        <CreateFedexSmartpostCarrierForm
          carrierInput={newCarrier.inputs.fedexSmartpostCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX_CROSS_BORDER:
      return (
        <CreateFedexCrossBorderCarrierForm
          carrierInput={newCarrier.inputs.fedexCrossBorderCarrierInput}
        />
      );
    case RedoCarrierName.VEHO:
      return (
        <CreateVehoCarrierForm
          carrierInput={newCarrier.inputs.vehoCarrierInput}
        />
      );
    case RedoCarrierName.APC:
      return (
        <CreateApcCarrierForm
          carrierInput={newCarrier.inputs.apcCarrierInput}
        />
      );
    case RedoCarrierName.FIRST_MILE:
      return (
        <CreateFirstMileCarrierForm
          carrierInput={newCarrier.inputs.firstMileCarrierInput}
        />
      );
    case RedoCarrierName.AUSTRALIA_POST:
      return (
        <CreateAustraliaPostCarrierForm
          carrierInput={newCarrier.inputs.australiaPostCarrierInput}
        />
      );
    case RedoCarrierName.CANADA_POST:
      return (
        <CreateCanadaPostCarrierForm
          carrierInput={newCarrier.inputs.canadaPostCarrierInput}
        />
      );
    case RedoCarrierName.CANADA_POST_DIRECT:
      return (
        <CreateCanadaPostDirectCarrierForm
          carrierInput={newCarrier.inputs.canadaPostDirectCarrierInput}
        />
      );
    case RedoCarrierName.ROYAL_MAIL:
      return (
        <CreateRoyalMailCarrierForm
          carrierInput={newCarrier.inputs.royalMailCarrierInput}
        />
      );
    case RedoCarrierName.ROYAL_MAIL_V3:
      return (
        <CreateRoyalMailV3CarrierForm
          carrierInput={newCarrier.inputs.royalMailV3CarrierInput}
        />
      );
    case RedoCarrierName.SEKO_OMNIPARCEL:
      return (
        <CreateSekoOmniParcelCarrierForm
          carrierInput={newCarrier.inputs.sekoOmniParcelCarrierInput}
        />
      );
    case RedoCarrierName.GLS:
      return (
        <CreateGlsCarrierForm
          carrierInput={newCarrier.inputs.glsCarrierInput}
        />
      );
    case RedoCarrierName.ASENDIA:
      return (
        <CreateAsendiaCarrierForm
          carrierInput={newCarrier.inputs.asendiaCarrierInput}
        />
      );
    case RedoCarrierName.SPEE_DEE:
      return (
        <CreateSpeeDeeCarrierForm
          carrierInput={newCarrier.inputs.speeDeeCarrierInput}
        />
      );
    case RedoCarrierName.AMAZON:
      return (
        <CreateAmazonCarrierForm
          carrierInput={newCarrier.inputs.amazonCarrierInput}
        />
      );
    case RedoCarrierName.USPS_SHIP:
      return (
        <CreateUspsShipCarrierForm
          carrierInput={newCarrier.inputs.uspsShipCarrierInput}
        />
      );
    case RedoCarrierName.USPS_DIRECT:
      return (
        <CreateUspsDirectCarrierForm
          carrierInput={newCarrier.inputs.uspsDirectCarrierInput}
        />
      );
    case RedoCarrierName.PASSPORT_GLOBAL:
      return (
        <CreatePassportGlobalCarrierForm
          carrierInput={newCarrier.inputs.passportGlobalCarrierInput}
        />
      );
    case RedoCarrierName.EPOST_GLOBAL:
      return (
        <CreateEPostGlobalCarrierForm
          carrierInput={newCarrier.inputs.epostGlobalCarrierInput}
        />
      );
    case RedoCarrierName.ETA_EXPRESS:
      return (
        <CreateETAExpressCarrierForm
          carrierInput={newCarrier.inputs.etaExpressCarrierInput}
        />
      );
    case RedoCarrierName.ONTRAC:
      return (
        <CreateOnTracCarrierForm
          carrierInput={newCarrier.inputs.onTracCarrierInput}
        />
      );
    case RedoCarrierName.PUROLATOR:
      return (
        <CreatePurolatorCarrierForm
          carrierInput={newCarrier.inputs.purolatorCarrierInput}
        />
      );
    case RedoCarrierName.CANPAR_EXPRESS:
      return (
        <CreateCanparCarrierForm
          carrierInput={newCarrier.inputs.canparCarrierInput}
        />
      );
    case RedoCarrierName.UNIUNI:
      return (
        <CreateUniUniCarrierForm
          carrierInput={newCarrier.inputs.uniUniCarrierInput}
        />
      );
    case RedoCarrierName.STALLION_EXPRESS:
      return (
        <CreateStallionExpressCarrierForm
          carrierInput={newCarrier.inputs.stallionExpressCarrierInput}
        />
      );
    case RedoCarrierName.ECOM_EXPRESS:
      return (
        <CreateEcomExpressCarrierForm
          carrierInput={newCarrier.inputs.ecomExpressCarrierInput}
        />
      );
    case RedoCarrierName.CHITCHATS:
      return (
        <CreateChitChatsCarrierForm
          carrierInput={newCarrier.inputs.chitChatsCarrierInput}
        />
      );
    case RedoCarrierName.SENDCLOUD:
      return (
        <CreateSendcloudCarrierForm
          carrierInput={newCarrier.inputs.sendcloudCarrierInput}
        />
      );
    case RedoCarrierName.JITSU:
      return (
        <CreateJitsuCarrierForm
          carrierInput={newCarrier.inputs.jitsuCarrierInput}
        />
      );
    default:
      assertNever(newCarrier.inputs.redoCarrierName.value);
  }
};

const CarrierCard = memo(function CarrierCard({
  carrier,
  onClick,
}: {
  carrier: RedoCarrierName;
  onClick: (carrier: RedoCarrierName) => void;
}) {
  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      cursor="pointer"
      flexDirection="column"
      gap="lg"
      hoverColor="secondary"
      onClick={() => onClick(carrier)}
      p="lg"
      radius="md"
      translate="yes"
    >
      <Flex dir="column" w="full">
        <Flex align="center" dir="row" gap="lg">
          <ShippingLogo
            carrier={carrierCardLogoCarrier(carrier)}
            size={LogoSize.MEDIUM}
          />
          <Text fontSize="sm" fontWeight="semibold">
            {carrierCardLabel(carrier)}
          </Text>
        </Flex>
        <Flex justify="flex-end" w="full">
          <ArrowRightIcon style={{ width: "16px", height: "16px" }} />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const AddCarrierForm = memo(function AddCarrierForm({
  newCarrier,
}: {
  newCarrier: CarrierInputForm;
}) {
  return (
    <Flex dir="column" gap="3xl" w="full">
      <RedoTextInput
        label="Account name"
        placeholder="Enter a name"
        setValue={newCarrier.inputs.name.setValue}
        value={newCarrier.inputs.name.value}
      />
      {carrierSpecificForm(newCarrier)}
    </Flex>
  );
});
